import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { environment } from './../../../../environments/environment';
import { QR_USER } from '../../../constants';
import { Router } from '@angular/router';
import dayjs from 'dayjs';

export enum LoginError {
	UNKNOWN_TENANT = 'UNKNOWN_TENANT',
	INVALID_EMAIL = 'INVALID_EMAIL',
	INVALID_PASSWORD = 'INVALID_PASSWORD',
	CHECK_EMAIL = 'CHECK_EMAIL',
	OTHER = 'OTHER',
}
@Injectable({ providedIn: 'root' })
export class AuthenticationService {
	private currentUserSubject: BehaviorSubject<any>;
	private loginErrorSubject: BehaviorSubject<any>;
	public currentUser: Observable<any>;
	public loginError: Observable<any>;
	public resultcode: any;
	public responseData: any = 'test';
	tenant;
	TOKEN_VALIDITY: number = 3599; // put 60 to test every minute

	constructor(
		private http: HttpClient,
		private router: Router,
	) {
		this.currentUserSubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem(QR_USER)));
		this.currentUser = this.currentUserSubject.asObservable();

		this.loginErrorSubject = new BehaviorSubject<string>(null);
		this.loginError = this.loginErrorSubject.asObservable();
	}

	public get currentUserValue() {
		return this.currentUserSubject.value;
	}

	public get accessToken() {
		return JSON.parse(localStorage.getItem(QR_USER))['accessToken'];
	}

	public get expired() {
		const expires = JSON.parse(localStorage.getItem(QR_USER))['expires'];
		return dayjs().diff(expires, 'seconds') > 0;
	}

	public get expiredRefreshToken() {
		const expires = JSON.parse(localStorage.getItem(QR_USER))['expires'];
		return dayjs().diff(expires, 'days') > 30;
	}

	refreshToken() {
		return JSON.parse(localStorage.getItem(QR_USER))['refreshToken'];
	}

	public get authenticated() {
		return !!localStorage.getItem(QR_USER);
	}

	public get deviceId(): string {
		let id = localStorage.getItem('_QR_DEVICE_ID');
		if (!id) {
			id = crypto.randomUUID();
			localStorage.setItem('_QR_DEVICE_ID', id);
		}
		return id;
	}

	public async login(tenantId: string, email: string, password: string) {
		return this.getToken(tenantId, email, password);
	}

	newLoginError(message) {
		this.loginErrorSubject.next(message);
	}

	public getToken(tenantId: string, email: string, password: string): Promise<void> {
		const url = environment.identityPlatformUrl;
		const suffix = environment.identityPlatformSignInSuffix;
		const key = environment.identityPlatformApiKey;
		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});
		return firstValueFrom(
			this.http.post<any>(
				`${url}${suffix}${key}`,
				{
					tenantId: tenantId,
					email: email,
					password: password,
					returnSecureToken: true,
				},
				{ headers },
			),
		)
			.then(
				(response) => {
					this.doLoginUser(tenantId, response.email, response.idToken, response.refreshToken, response.localId, response.displayName);
				},
				(err) => {
					if (err.error.error.message === 'INVALID_EMAIL' || err.error.error.message === 'EMAIL_NOT_FOUND') {
						this.newLoginError(LoginError.INVALID_EMAIL);
					} else if (err.error.error.message === 'INVALID_PASSWORD') {
						this.newLoginError(LoginError.INVALID_PASSWORD);
					} else {
						this.newLoginError(err.error.error.message);
					}
					throw err;
				},
			);
	}

	//send send an api request to reset the password for the tennant with this id, mail will be send to this Email
	public sendResetPasswordMail(email: String, tenantId: string): Observable<any> {
		const url = environment.identityPlatformUrl;
		const suffix = environment.identityPlatformResetPasswordSuffix;
		const key = environment.identityPlatformApiKey;
		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});
		return this.http.post<any>(
			`${url}${suffix}${key}`,
			{
				requestType: 'PASSWORD_RESET',
				email: email,
				tenantId: tenantId,
			},
			{ headers },
		);
	}

	// Feature 231: request a passwordless sign-in ("magic") link. The backend
	// generates the link and sends it from the operator's own sender. Public
	// (allow-listed) operation — no auth token needed. Always resolves (neutral),
	// so the UI must never reveal whether the address exists.
	public requestLoginLink(tenantId: string, email: string): Promise<any> {
		const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
		const query = `mutation sendLoginLink($tenantId: ID!, $email: String!) {
			sendLoginLink(tenantId: $tenantId, email: $email) { accepted throttled }
		}`;
		return firstValueFrom(
			this.http.post<any>(environment.apiUrl, { query, variables: { tenantId, email } }, { headers }),
		);
	}

	// Feature 231: request a password-reset link delivered from the operator's own
	// sender (replaces the direct Identity Platform PASSWORD_RESET call). Public.
	public requestPasswordResetLink(tenantId: string, email: string): Promise<any> {
		const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
		const query = `mutation sendPasswordResetLink($tenantId: ID!, $email: String!) {
			sendPasswordResetLink(tenantId: $tenantId, email: $email) { accepted throttled }
		}`;
		return firstValueFrom(
			this.http.post<any>(environment.apiUrl, { query, variables: { tenantId, email } }, { headers }),
		);
	}

	// Feature 231: complete a magic-link sign-in by exchanging the oobCode from the
	// emailed link for tokens, then establish the session exactly like password login
	// (reuses doLoginUser → persistent 30-day refresh-token session).
	// On 2FA tenants the link also carries a one-time `twoFAToken`; it is redeemed BEFORE the
	// session is published, otherwise the app's first queries hit TWO_FA_REQUIRED and log out.
	public completeEmailLinkSignIn(tenantId: string, email: string, oobCode: string, twoFAToken?: string): Promise<void> {
		const url = environment.identityPlatformUrl;
		const suffix = environment.identityPlatformEmailLinkSuffix;
		const key = environment.identityPlatformApiKey;
		const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
		return firstValueFrom(
			this.http.post<any>(
				`${url}${suffix}${key}`,
				{
					tenantId: tenantId,
					email: email,
					oobCode: oobCode,
					returnSecureToken: true,
				},
				{ headers },
			),
		).then(
			async (response) => {
				if (twoFAToken) {
					await this.trustDeviceWithEmailLinkToken(response.idToken, twoFAToken);
				}
				this.doLoginUser(tenantId, response.email, response.idToken, response.refreshToken, response.localId, response.displayName);
			},
			(err) => {
				this.newLoginError(LoginError.OTHER);
				throw err;
			},
		);
	}

	public async getNewToken() {
		const url = environment.identityPlatformUrl;
		const suffix = environment.identityPlatformRefreshTokenSuffix;
		const key = environment.identityPlatformApiKey;
		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});

		try {
			const response = await firstValueFrom(
				this.http.post<any>(
					`${url}${suffix}${key}`,
					{
						grant_type: 'refresh_token',
						refresh_token: this.refreshToken(),
					},
					{ headers },
				),
			);
			const user = JSON.parse(localStorage.getItem(QR_USER));
			user.accessToken = response.access_token;
			user.refreshToken = response.refresh_token;
			user.expires = dayjs().add(this.TOKEN_VALIDITY, 'seconds');
			localStorage.setItem(QR_USER, JSON.stringify(user));
		} catch (err) {
			this.logout();
		}
	}

	// Best-effort: if it fails, the normal 2FA code flow still applies after login.
	private async trustDeviceWithEmailLinkToken(idToken: string, twoFAToken: string): Promise<void> {
		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
			Authorization: `Bearer ${idToken}`,
			'X-Device-ID': this.deviceId,
		});
		const query = `mutation verifyTwoFACode($code: String!, $deviceId: String!) {
			verifyTwoFACode(code: $code, deviceId: $deviceId)
		}`;
		try {
			await firstValueFrom(
				this.http.post<any>(environment.apiUrl, { query, variables: { code: twoFAToken, deviceId: this.deviceId } }, { headers }),
			);
		} catch {
			/* ignore — see above */
		}
	}

	private doLoginUser(tenantId: string, email: string, accessToken: string, refreshToken: string, userRef: string, displayName: string) {
		const user = {
			accessToken: accessToken,
			refreshToken: refreshToken,
			email: email,
			tenantId: tenantId,
			userRef,
			displayName,
			expires: dayjs().add(this.TOKEN_VALIDITY, 'seconds'),
		};
		localStorage.setItem(QR_USER, JSON.stringify(user));
		localStorage.removeItem('_QR_CURRENT_URL');
		this.currentUserSubject.next(user);
		this.newLoginError(null);
	}

	// Feature 231: adopt a session that another tab stored (e.g. the tab opened by a magic link).
	adoptStoredUser() {
		const user = JSON.parse(localStorage.getItem(QR_USER));
		if (user) {
			this.currentUserSubject.next(user);
		}
	}

	logout() {
		localStorage.removeItem(QR_USER);
		this.currentUserSubject.next(null);
		this.router.navigate(['/account/login']);
	}
}
